import { useMemo, useState } from "react";
import { maxProfileSkills, presetSkillOptions } from "../../../constants/skills";

type SkillSelectorProps = {
  label: string;
  helperText?: string;
  selectedSkills: string[];
  onChange: (skills: string[]) => void;
  idPrefix: string;
};

const normalizeSkill = (value: string) => value.trim().replace(/\s+/g, " ");

const SkillSelector = ({ label, helperText, selectedSkills, onChange, idPrefix }: SkillSelectorProps) => {
  const [customSkill, setCustomSkill] = useState("");
  const selectedSkillSet = useMemo(() => new Set(selectedSkills), [selectedSkills]);

  const addSkill = (skill: string) => {
    const normalizedSkill = normalizeSkill(skill);
    if (!normalizedSkill || selectedSkillSet.has(normalizedSkill) || selectedSkills.length >= maxProfileSkills) {
      return;
    }
    onChange([...selectedSkills, normalizedSkill]);
  };

  const removeSkill = (skill: string) => {
    onChange(selectedSkills.filter((entry) => entry !== skill));
  };

  const togglePreset = (skill: string) => {
    if (selectedSkillSet.has(skill)) {
      removeSkill(skill);
      return;
    }
    addSkill(skill);
  };

  const addCustomSkill = () => {
    addSkill(customSkill);
    setCustomSkill("");
  };

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-myteengig-text-primary">{label}</label>
      {helperText ? <p className="mb-3 text-sm text-myteengig-text-muted">{helperText}</p> : null}

      <div className="max-h-28 overflow-y-auto pr-1">
        <div className="flex flex-wrap gap-2">
        {presetSkillOptions.map((skill) => {
          const selected = selectedSkillSet.has(skill);
          return (
            <button
              key={skill}
              id={`${idPrefix}-${skill.toLowerCase().replace(/\s+/g, "-")}`}
              type="button"
              onClick={() => togglePreset(skill)}
              className={`rounded-full border px-3 py-2 text-sm font-medium transition ${
                selected
                  ? "border-myteengig-primary bg-myteengig-primary text-white"
                  : "border-myteengig-border bg-white text-myteengig-text-primary hover:border-myteengig-primary hover:text-myteengig-primary"
              }`}
            >
              {skill}
            </button>
          );
        })}
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <input
          id={`${idPrefix}-custom-skill`}
          type="text"
          maxLength={60}
          value={customSkill}
          onChange={(event) => setCustomSkill(event.target.value)}
          className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
          placeholder="Add another skill"
        />
        <button
          type="button"
          onClick={addCustomSkill}
          disabled={selectedSkills.length >= maxProfileSkills}
          className="rounded-md border border-myteengig-border bg-white px-4 py-2 text-sm font-semibold text-myteengig-text-primary transition hover:border-myteengig-primary hover:text-myteengig-primary disabled:cursor-not-allowed disabled:opacity-60"
        >
          Add
        </button>
      </div>

      <div className="mt-3 flex min-h-8 flex-wrap gap-2">
        {selectedSkills.map((skill) => (
          <button
            key={skill}
            type="button"
            onClick={() => removeSkill(skill)}
            className="rounded-full border border-myteengig-primary/30 bg-myteengig-background px-3 py-1 text-sm text-myteengig-text-primary"
          >
            {skill} x
          </button>
        ))}
      </div>

      <p className="mt-2 text-xs text-myteengig-text-muted">
        Choose up to {maxProfileSkills} skills. You can remove a skill by clicking its tag.
      </p>
    </div>
  );
};

export default SkillSelector;
